/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import './SolutionPopover.scss';
import Popover from '@react/react-spectrum/Popover';
import React, {lazy, Suspense, useEffect} from 'react';
import Wait from '@react/react-spectrum/Wait';

const SolutionSwitcher = lazy(() => import('./SolutionSwitcher'));

/**
 * Solution Popover functional component.
 * @param {Object} <ObjectPattern> The below props.
 * Prop: {boolean} open - Determines if solution switcher is open or not.
 * Prop: {Object} props - Other props to pass down from parent.
 * @returns {ReactElement} Markup representing the SolutionPopover component.
 * @class
 */
export default function SolutionPopover({open, ...props}) {
  const {
    closeMenu,
    locale,
    offset,
    placement,
    style,
    ...switcherProps
  } = props;

  // Focus on the solution switcher container so first tab will select correct element
  open && document.getElementById('solutionSwitcher').focus();
  // Hack to enable correct escape key functionality
  useEffect(() => {
    function escapeMenu(e) {
      e.keyCode === 27 && closeMenu();
    }
    function onClickCloseMenu({target}) {
      // Close the menu unless the click is within the solution switcher or solution dialog.
      target.closest('.shellSolutionSwitcher') || closeMenu();
    }
    window.addEventListener('keydown', escapeMenu);
    window.addEventListener('click', onClickCloseMenu);
    return () => {
      window.removeEventListener('keydown', escapeMenu);
      window.removeEventListener('click', onClickCloseMenu);
    };
  });

  return (
    <Popover
      className="solutionContainer shellSolutionSwitcher"
      id="solutionSwitcher"
      isDialog={false}
      offset={offset}
      open={open}
      placement={placement}
      role="dialog"
      style={{...style, left: 0, right: 0, top: 42, zIndex: 10015}}
    >
      <Suspense fallback={<Wait centered />}>
        <SolutionSwitcher closeMenu={closeMenu} locale={locale} {...switcherProps} />
      </Suspense>
    </Popover>
  );
}
